// Copyright (c) Ping. All rights reserved.
//
// Business Directory — Figma node 541:37846.
// Layout: left filter sidebar (336px) + scrollable card grid.
// All colours come from design-token classes + arbitrary-value classes for
// exact Figma values without a token (per figma-token-mismatches.md).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Globe,
  MapPin,
  Search,
  SlidersHorizontal,
  Users,
  X,
} from 'lucide-react';

import type { Category, Company, CompanyListFilters, EmployeeRange } from './directoryApi';
import {   getCategories,
  getCompanies,
resolveCompanyLogoUrl ,
} from './directoryApi';
import {
  cacheCategories,
  cacheDirectoryCompanies,
  readCachedCategories,
  readCachedDirectoryCompanies,
  useMyCompanies,
} from './useBusinessDirectory';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BusinessDirectoryViewProps {
  onCreateCompany: () => void;
  onCompanySelect?: (company: Company) => void;
  /** Currently-authed user's account id — used to detect own companies. */
  currentAccountId?: string;
}

const EMPLOYEE_RANGES: { label: string; value: EmployeeRange }[] = [
  { label: '1 – 10', value: '1-10' },
  { label: '11 – 50', value: '11-50' },
  { label: '51 – 200', value: '51-200' },
  { label: '201 – 500', value: '201-500' },
  { label: '501 – 1 000', value: '501-1000' },
  { label: '1 000+', value: '1000+' },
];

const PAGE_SIZE = 24;

// ─── Company logo / avatar ─────────────────────────────────────────────────────

function CompanyAvatar({
  name,
  logoUrl,
  size = 'md',
}: {
  name: string;
  logoUrl: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim = size === 'sm' ? 'w-8 h-8' : size === 'lg' ? 'w-16 h-16' : 'w-10 h-10';
  const textSize = size === 'sm' ? 'text-[11px]' : size === 'lg' ? 'text-xl' : 'text-sm';
  const radius = size === 'lg' ? 'rounded-2xl' : 'rounded-xl';

  // Fall back to the initials tile when the logo URL is broken or expired
  // (a dead src would otherwise render the browser's broken-image glyph).
  const [imgFailed, setImgFailed] = useState(false);
  // Uploaded logos are stored as a stable `media:<id>` ref; resolve it to a
  // fresh presigned URL (external http(s) logos pass through unchanged).
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  useEffect(() => {
    setImgFailed(false);
    if (!logoUrl) {
      setResolvedSrc(null);
      return;
    }
    let cancelled = false;
    setResolvedSrc(null);
    void resolveCompanyLogoUrl(logoUrl).then((url) => {
      if (!cancelled) setResolvedSrc(url ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl]);

  if (resolvedSrc && !imgFailed) {
    return (
      <img
        src={resolvedSrc}
        alt={name}
        onError={() => setImgFailed(true)}
        className={`${dim} ${radius} object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div
      className={`${dim} ${radius} flex-shrink-0 flex items-center justify-center
        bg-light-surface-tertiary dark:bg-dark-surface-secondary font-bold uppercase
        text-light-text-secondary dark:text-[#B5BAC3] ${textSize}`}
    >
      {name.charAt(0) || '?'}
    </div>
  );
}

// ─── Company card ──────────────────────────────────────────────────────────────

function CompanyCard({
  company,
  onClick,
}: {
  company: Company;
  onClick: () => void;
}) {
  const location = [company.locationCity, company.locationCountry]
    .filter(Boolean)
    .join(', ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="
        w-full text-left p-4 rounded-xl flex flex-col gap-3
        bg-white dark:bg-dark-surface-primary
        border border-light-border-subtle dark:border-[#2C2C2E]
        hover:border-light-accent-indigo/40 dark:hover:border-[#5C7BF0]/30
        hover:bg-light-surface-secondary dark:hover:bg-[#222429]
        transition-colors duration-150 cursor-pointer
      "
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[15px] font-semibold leading-tight text-light-text-primary dark:text-[#F5F6F8] truncate">
              {company.name}
            </span>
            {company.verified && (
              <CheckCircle2
                size={14}
                className="flex-shrink-0 text-light-accent-indigo dark:text-[#5C7BF0]"
                aria-label="Verified"
              />
            )}
          </div>
          {location && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={11} className="text-light-text-tertiary dark:text-dark-text-muted flex-shrink-0" />
              <span className="text-[12px] text-light-text-tertiary dark:text-dark-text-muted truncate">
                {location}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      {company.categories.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {company.categories.slice(0, 3).map((cat) => (
            <span
              key={cat.slug}
              className="
                px-2 py-0.5 rounded-full text-[11px] font-medium
                bg-light-surface-tertiary dark:bg-dark-surface-secondary
                text-light-text-secondary dark:text-[#B5BAC3]
              "
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {/* Description */}
      {company.description && (
        <p className="text-[13px] text-light-text-secondary dark:text-dark-text-tertiary leading-snug line-clamp-2 m-0">
          {company.description}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-1">
        {company.employeeRange ? (
          <div className="flex items-center gap-1">
            <Users size={11} className="text-light-text-tertiary dark:text-dark-text-muted" />
            <span className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted">
              {company.employeeRange} employees
            </span>
          </div>
        ) : (
          <span />
        )}
        <ChevronRight size={14} className="text-light-text-tertiary dark:text-dark-text-muted" />
      </div>
    </button>
  );
}

// ─── Company detail modal ──────────────────────────────────────────────────────

function CompanyDetailModal({
  company,
  onClose,
}: {
  company: Company;
  onClose: () => void;
}) {
  const location = [company.locationCity, company.locationCountry]
    .filter(Boolean)
    .join(', ');

  // Close on backdrop click
  const backdropRef = useRef<HTMLDivElement>(null);
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === backdropRef.current) onClose();
    },
    [onClose],
  );

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={company.name}
    >
      <div
        className="
          relative w-full max-w-[900px] mx-4
          bg-light-surface-primary dark:bg-dark-surface-primary
          border border-light-border-subtle dark:border-dark-border-strong
          rounded-xl overflow-hidden shadow-2xl
        "
        style={{ maxHeight: '85vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-light-border-subtle dark:border-dark-border-strong">
          <div className="flex items-center gap-3">
            <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size="lg" />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-light-text-primary dark:text-[#EBEBEB] m-0">
                  {company.name}
                </h2>
                {company.verified && (
                  <CheckCircle2
                    size={16}
                    className="text-light-accent-indigo dark:text-[#5C7BF0]"
                    aria-label="Verified business"
                  />
                )}
              </div>
              {company.sector && (
                <p className="text-sm text-light-text-secondary dark:text-[#B3B3B3] m-0">
                  {company.sector}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="
              w-8 h-8 rounded-lg flex items-center justify-center
              text-light-text-tertiary dark:text-[#B3B3B3]
              hover:bg-light-surface-secondary dark:hover:bg-[#2C2C2E]
              transition-colors
            "
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex overflow-y-auto" style={{ maxHeight: 'calc(85vh - 73px)' }}>
          {/* Left column */}
          <div className="w-[280px] flex-shrink-0 p-6 border-r border-light-border-subtle dark:border-dark-border-strong flex flex-col gap-4">
            {location && (
              <div className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 flex-shrink-0 text-light-text-tertiary dark:text-dark-text-muted" />
                <div>
                  <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted uppercase tracking-wide m-0 mb-0.5">
                    Location
                  </p>
                  <p className="text-sm text-light-text-primary dark:text-[#F5F6F8] m-0">
                    {location}
                  </p>
                </div>
              </div>
            )}

            {company.employeeRange && (
              <div className="flex items-start gap-2">
                <Users size={14} className="mt-0.5 flex-shrink-0 text-light-text-tertiary dark:text-dark-text-muted" />
                <div>
                  <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted uppercase tracking-wide m-0 mb-0.5">
                    Team size
                  </p>
                  <p className="text-sm text-light-text-primary dark:text-[#F5F6F8] m-0">
                    {company.employeeRange} employees
                  </p>
                </div>
              </div>
            )}

            {company.website && (
              <div className="flex items-start gap-2">
                <Globe size={14} className="mt-0.5 flex-shrink-0 text-light-text-tertiary dark:text-dark-text-muted" />
                <div>
                  <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted uppercase tracking-wide m-0 mb-0.5">
                    Website
                  </p>
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-light-accent-indigo dark:text-[#5C7BF0] hover:underline flex items-center gap-1"
                  >
                    {company.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            )}

            {company.foundedYear && (
              <div>
                <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted uppercase tracking-wide m-0 mb-0.5">
                  Founded
                </p>
                <p className="text-sm text-light-text-primary dark:text-[#F5F6F8] m-0">
                  {company.foundedYear}
                </p>
              </div>
            )}

            {company.categories.length > 0 && (
              <div>
                <p className="text-[11px] text-light-text-tertiary dark:text-dark-text-muted uppercase tracking-wide m-0 mb-2">
                  Categories
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {company.categories.map((cat) => (
                    <span
                      key={cat.slug}
                      className="
                        px-2 py-0.5 rounded-full text-[11px] font-medium
                        bg-light-surface-tertiary dark:bg-dark-surface-secondary
                        text-light-text-secondary dark:text-[#B5BAC3]
                      "
                    >
                      {cat.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="flex-1 p-6 flex flex-col gap-5">
            {company.description && (
              <div>
                <h3 className="text-sm font-semibold text-light-text-primary dark:text-[#EBEBEB] mb-2">
                  About
                </h3>
                <p className="text-sm text-light-text-secondary dark:text-[#B3B3B3] leading-relaxed m-0 whitespace-pre-wrap">
                  {company.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Filter sidebar ────────────────────────────────────────────────────────────

function FilterSection({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-light-border-subtle dark:border-dark-border-subtle last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3 text-sm font-semibold text-light-text-primary dark:text-[#F5F6F8] cursor-pointer"
      >
        {title}
        <ChevronDown
          size={14}
          className={`text-light-text-tertiary dark:text-dark-text-muted transition-transform ${open ? 'rotate-0' : '-rotate-90'}`}
        />
      </button>
      {open && <div className="pb-3">{children}</div>}
    </div>
  );
}

// ─── Main view ─────────────────────────────────────────────────────────────────

export function BusinessDirectoryView({
  onCreateCompany,
  onCompanySelect,
}: BusinessDirectoryViewProps) {
  const [companies, setCompanies] = useState<readonly Company[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [categories, setCategories] = useState<readonly Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [selectedEmployeeRange, setSelectedEmployeeRange] = useState<EmployeeRange | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);
  const [debouncedLocation, setDebouncedLocation] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedLocation(locationFilter), 350);
    return () => clearTimeout(t);
  }, [locationFilter]);

  // Load categories on mount — seed from the offline cache first so the
  // filter sidebar renders instantly (and works fully offline), then refresh
  // from the network and re-cache.
  useEffect(() => {
    let cancelled = false;
    void readCachedCategories().then((cached) => {
      if (!cancelled && cached.length > 0) setCategories((prev) => (prev.length === 0 ? cached : prev));
    });
    getCategories()
      .then((cats) => {
        if (cancelled) return;
        setCategories(cats);
        void cacheCategories(cats);
      })
      .catch(() => { /* non-fatal — cached categories remain */ });
    return () => {
      cancelled = true;
    };
  }, []);

  // Build filters object
  const filters = useMemo<CompanyListFilters>(() => ({
    limit: PAGE_SIZE,
    page,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(debouncedLocation ? { locationCity: debouncedLocation } : {}),
    ...(selectedEmployeeRange ? { employeeRange: selectedEmployeeRange } : {}),
    ...(selectedCategories.size > 0 ? { categories: [...selectedCategories] } : {}),
  }), [debouncedSearch, debouncedLocation, selectedEmployeeRange, selectedCategories, page]);

  // Fetch companies whenever filters change. Offline-first: seed from the
  // Dexie cache immediately, then fetch from the network. On a network/server
  // error, fall back to the cached listing instead of only showing an error,
  // and mirror every successful page into the cache for the next visit.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Instant paint from cache (repeat / offline visits).
    void readCachedDirectoryCompanies(filters).then((cached) => {
      if (cancelled || cached.length === 0) return;
      setCompanies((prev) => (prev.length === 0 ? cached : prev));
      setLoading(false);
    });

    getCompanies(filters)
      .then(({ companies: list, total: t }) => {
        if (cancelled) return;
        setCompanies(list);
        setTotal(t);
        void cacheDirectoryCompanies(list);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        void readCachedDirectoryCompanies(filters).then((cached) => {
          if (cancelled) return;
          if (cached.length > 0) {
            setCompanies(cached);
            setTotal(cached.length);
            setError(null);
          } else {
            setError(err.message ?? 'Failed to load companies.');
          }
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  // Offline-first: the owner's own companies (Dexie liveQuery + optimistic
  // writes on create). The PUBLIC listing above only returns VERIFIED
  // companies, so a freshly-created/unverified company would never appear —
  // we surface the owner's own ones (matching the active filters) at the top
  // of page 1, deduped against the public results.
  const { companies: myCompanies } = useMyCompanies();

  const ownMatching = useMemo<readonly Company[]>(
    () =>
      myCompanies.filter((c) => {
        if (debouncedSearch) {
          const q = debouncedSearch.toLowerCase();
          if (!c.name.toLowerCase().includes(q) && !c.description.toLowerCase().includes(q)) {
            return false;
          }
        }
        if (
          debouncedLocation &&
          (c.locationCity ?? '').toLowerCase() !== debouncedLocation.toLowerCase()
        ) {
          return false;
        }
        if (selectedEmployeeRange && c.employeeRange !== selectedEmployeeRange) return false;
        if (selectedCategories.size > 0) {
          const slugs = c.categories.map((cat) => cat.slug);
          if (![...selectedCategories].some((s) => slugs.includes(s))) return false;
        }
        return true;
      }),
    [myCompanies, debouncedSearch, debouncedLocation, selectedEmployeeRange, selectedCategories],
  );

  const displayCompanies = useMemo<readonly Company[]>(() => {
    if (page !== 1) return companies;
    const ownIds = new Set(ownMatching.map((c) => c.id));
    return [...ownMatching, ...companies.filter((c) => !ownIds.has(c.id))];
  }, [page, ownMatching, companies]);

  // Reset page to 1 whenever filters (not page) change
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const filtersChanged =
      prev.search !== filters.search ||
      prev.locationCity !== filters.locationCity ||
      prev.employeeRange !== filters.employeeRange ||
      JSON.stringify(prev.categories) !== JSON.stringify(filters.categories);
    if (filtersChanged) setPage(1);
    prevFiltersRef.current = filters;
  }, [filters]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters =
    selectedCategories.size > 0 || selectedEmployeeRange !== null || debouncedLocation !== '';

  const handleClearFilters = () => {
    setSelectedCategories(new Set());
    setSelectedEmployeeRange(null);
    setLocationFilter('');
  };

  const toggleCategory = useCallback((slug: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) { next.delete(slug); } else { next.add(slug); }
      return next;
    });
  }, []);

  const handleCompanyClick = useCallback(
    (company: Company) => {
      setSelectedCompany(company);
      onCompanySelect?.(company);
    },
    [onCompanySelect],
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-light-surface-primary dark:bg-dark-surface-app">
      {/* ── Top header ── */}
      <div className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-primary dark:bg-dark-surface-panel">
        <div className="flex-1 relative max-w-[480px]">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-light-text-tertiary dark:text-[#808080] pointer-events-none"
          />
          <input
            type="search"
            placeholder="Search companies…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="
              w-full h-9 pl-9 pr-3 rounded-xl text-sm
              bg-light-surface-secondary dark:bg-dark-surface-primary
              text-light-text-primary dark:text-[#FAFAFA]
              placeholder:text-light-text-tertiary dark:placeholder:text-[#808080]
              border border-transparent focus:border-light-accent-indigo/30 dark:focus:border-[#5C7BF0]/30
              outline-none transition-colors
            "
          />
        </div>

        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className={`
            flex items-center gap-1.5 h-9 px-3 rounded-xl text-sm font-medium transition-colors
            ${filtersOpen
              ? 'bg-light-accent-indigo/10 dark:bg-[#5C7BF0]/15 text-light-accent-indigo dark:text-[#5C7BF0]'
              : 'bg-light-surface-secondary dark:bg-dark-surface-primary text-light-text-secondary dark:text-[#B5BAC3] hover:text-light-text-primary dark:hover:text-[#F5F6F8]'
            }
          `}
        >
          <SlidersHorizontal size={14} />
          Filters
          {hasActiveFilters && (
            <span className="w-4 h-4 rounded-full bg-light-accent-indigo dark:bg-[#537EFF] text-white text-[10px] font-bold flex items-center justify-center">
              {selectedCategories.size + (selectedEmployeeRange ? 1 : 0) + (debouncedLocation ? 1 : 0)}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onCreateCompany}
          className="
            flex items-center gap-1.5 h-9 px-4 rounded-xl text-sm font-semibold
            bg-[#537EFF] hover:bg-[#4A72F5] text-white
            transition-colors flex-shrink-0
          "
        >
          <Building2 size={14} />
          Add Company
        </button>
      </div>

      {/* ── Body: sidebar + grid ── */}
      <div className="flex-1 flex min-h-0">
        {/* Filter sidebar */}
        {filtersOpen && (
          <aside
            className="
              w-[280px] flex-shrink-0 overflow-y-auto border-r
              border-light-border-subtle dark:border-dark-border-subtle
              bg-light-surface-primary dark:bg-dark-surface-panel
            "
          >
            <div className="px-4 pt-4 pb-2 flex items-center justify-between">
              <span className="text-sm font-semibold text-light-text-primary dark:text-[#F5F6F8]">
                Filters
              </span>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="text-[12px] text-light-accent-indigo dark:text-[#5C7BF0] hover:underline"
                >
                  Reset all
                </button>
              )}
            </div>

            <div className="px-4">
              {/* Location filter */}
              <FilterSection title="Location">
                <div className="relative">
                  <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-text-tertiary dark:text-[#808080] pointer-events-none" />
                  <input
                    type="text"
                    placeholder="City or country…"
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="
                      w-full h-8 pl-8 pr-3 rounded-lg text-[13px]
                      bg-light-surface-secondary dark:bg-dark-surface-primary
                      text-light-text-primary dark:text-[#FAFAFA]
                      placeholder:text-light-text-tertiary dark:placeholder:text-[#808080]
                      border border-transparent focus:border-light-accent-indigo/30 dark:focus:border-[#5C7BF0]/30
                      outline-none transition-colors
                    "
                  />
                </div>
              </FilterSection>

              {/* Categories */}
              {categories.length > 0 && (
                <FilterSection title="Category">
                  <div className="flex flex-col gap-1.5">
                    {categories.map((cat) => (
                      <label
                        key={cat.slug}
                        className="flex items-center gap-2.5 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.has(cat.slug)}
                          onChange={() => toggleCategory(cat.slug)}
                          className="w-3.5 h-3.5 rounded border-light-border-subtle dark:border-dark-border-strong accent-[#537EFF]"
                        />
                        <span className="text-[13px] text-light-text-secondary dark:text-[#B5BAC3] group-hover:text-light-text-primary dark:group-hover:text-[#F5F6F8] transition-colors">
                          {cat.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </FilterSection>
              )}

              {/* Employee range */}
              <FilterSection title="Team size">
                <div className="flex flex-col gap-1.5">
                  {EMPLOYEE_RANGES.map(({ label, value }) => (
                    <label key={value} className="flex items-center gap-2.5 cursor-pointer group">
                      <input
                        type="radio"
                        name="employeeRange"
                        checked={selectedEmployeeRange === value}
                        onChange={() =>
                          setSelectedEmployeeRange((prev) => (prev === value ? null : value))
                        }
                        className="w-3.5 h-3.5 accent-[#537EFF]"
                      />
                      <span className="text-[13px] text-light-text-secondary dark:text-[#B5BAC3] group-hover:text-light-text-primary dark:group-hover:text-[#F5F6F8] transition-colors">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </FilterSection>
            </div>
          </aside>
        )}

        {/* Main content area */}
        <main className="flex-1 min-w-0 overflow-y-auto p-6">
          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[160px] rounded-xl bg-light-surface-secondary dark:bg-dark-surface-primary animate-pulse"
                />
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
              <Building2
                size={40}
                strokeWidth={1.2}
                className="text-light-text-tertiary dark:text-dark-text-muted"
              />
              <p className="text-sm text-light-text-secondary dark:text-[#B5BAC3]">{error}</p>
              <button
                type="button"
                onClick={() => setPage(1)}
                className="text-sm text-light-accent-indigo dark:text-[#5C7BF0] hover:underline"
              >
                Retry
              </button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && displayCompanies.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-light-surface-secondary dark:bg-dark-surface-primary">
                <Building2
                  size={28}
                  strokeWidth={1.5}
                  className="text-light-text-tertiary dark:text-dark-text-muted"
                />
              </div>
              <div className="flex flex-col gap-1 max-w-xs">
                <h3 className="text-base font-semibold text-light-text-primary dark:text-[#F5F6F8] m-0">
                  {hasActiveFilters || debouncedSearch ? 'No results' : 'No companies yet'}
                </h3>
                <p className="text-sm text-light-text-secondary dark:text-dark-text-tertiary m-0">
                  {hasActiveFilters || debouncedSearch
                    ? 'Try adjusting your filters or search term.'
                    : 'Be the first to add your company to the directory.'}
                </p>
              </div>
              {!hasActiveFilters && !debouncedSearch && (
                <button
                  type="button"
                  onClick={onCreateCompany}
                  className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-sm font-semibold bg-[#537EFF] hover:bg-[#4A72F5] text-white transition-colors"
                >
                  <Building2 size={14} />
                  Add your company
                </button>
              )}
            </div>
          )}

          {/* Company grid */}
          {!loading && !error && displayCompanies.length > 0 && (
            <>
              <div className="mb-4 flex items-center justify-between">
                <p className="text-[13px] text-light-text-tertiary dark:text-dark-text-muted">
                  {displayCompanies.length} {displayCompanies.length === 1 ? 'company' : 'companies'}
                </p>
              </div>

              <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
                {displayCompanies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    onClick={() => handleCompanyClick(company)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="h-8 px-3 rounded-lg text-sm border border-light-border-subtle dark:border-dark-border-strong text-light-text-secondary dark:text-[#B5BAC3] disabled:opacity-40 hover:bg-light-surface-secondary dark:hover:bg-dark-surface-primary transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-light-text-tertiary dark:text-dark-text-muted">
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="h-8 px-3 rounded-lg text-sm border border-light-border-subtle dark:border-dark-border-strong text-light-text-secondary dark:text-[#B5BAC3] disabled:opacity-40 hover:bg-light-surface-secondary dark:hover:bg-dark-surface-primary transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Company detail modal */}
      {selectedCompany && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
        />
      )}
    </div>
  );
}
